<template>
  <div class="resource-specific">
    <div class="container main-info">
      <Breadcrumb
        classes="light scrollable"
        :breadcrumb="breadcrumbPcs"
      />
      <div class="resource-info">
        <div class="image-wrapper" :class="{ extended: resource.categorySlug === 'practice-exams' }">
          <div class="left-group">
            <img class="image" :src="`${$config.imagesUrl}/resources/${resource.image}`" :alt="resource.imageAlt">
            <DownloadForm
              key="download1"
              hide-fields
              :category="resource.categorySlug"
              :exam="resource.categorySlug === 'practice-exams'"
              :exam-id="resource.examId"
              data-event="download1.click"
            />
          </div>
          <div class="icons">
            <a
              class="social"
              title="Share Twitter"
              data-event="sharetw.click"
              data-direct-send
              @click.prevent="shareTwitter(url)"
            >
              <TwitterLogo />
            </a>
            <a
              class="social"
              title="Share Facebook"
              data-event="sharefb.click"
              data-direct-send
              @click.prevent="shareFacebook(url)"
            >
              <FacebookLogo />
            </a>
            <a
              class="social"
              title="Share Linkedin"
              data-event="shareln.click"
              data-direct-send
              @click.prevent="shareLinkedin(url)"
            >
              <LinkedinLogo />
            </a>
          </div>
        </div>
        <div class="badges">
          <nuxt-link class="cat" :to="`/resources-center/${resource.categorySlug}/`">
            {{ resource.categoryName }}
          </nuxt-link>
          <nuxt-link
            v-if="resource.technology" class="technology" :class="resource.technologySlug"
            :to="`/resources-center/${resource.categorySlug}?technology=${resource.technologySlug}`"
          >
            {{ resource.technology }}
          </nuxt-link>
        </div>
        <h1 class="h2">
          {{ resource.name }}
        </h1>
        <h2 class="h4">
          {{ resource.subname }}
        </h2>
        <div class="instructor-card">
          <img
            class="card-image"
            :src="`${$config.imagesUrl}/team/thumb@128_${resource.author.image}`"
            :alt="resource.author.name"
          >
          <div class="card-section">
            <p class="h6">
              {{ resource.author.name }}
              <a
                v-if="resource.author.linkedin"
                target="_blank"
                :href="resource.author.linkedin"
                rel="nofollow"
                class="social-link"
              >
                <LinkedinLogo />
              </a>
            </p>
            <span v-if="resource.author.title" class="founder-info">{{ resource.author.title }}</span>
          </div>
        </div>
        <div class="summary body-2" v-html="resource.description" />
      </div>
      <div class="our-mission tracking-section" data-event="whoisitfor.scroll">
        <h2 class="h3">
          Who is it for
        </h2>
        <div class="mission-description body-1" v-html="resource.forDescription" />
      </div>
      <div class="benefits tracking-section" data-event="howitwillhelpyou.scroll">
        <h2 class="h3">
          How it can help you
        </h2>
        <div class="body-1" v-html="resource.helpDescription" />
      </div>
    </div>
    <div v-if="resource.courses.length || resource.popular.length" class="know-stuff">
      <div v-if="resource.courses.length" class="container found-courses tracking-section" data-event="relatedcourses.scroll">
        <Slider class="slider slider-found" dark>
          <h3 slot="heading" class="h3">
            Courses where you'll use this resource
          </h3>
          <Card
            v-for="course in resource.courses"
            :key="course.id"
            slot="slide"
            dark
            :course="{ ...course, type: 'similar' }"
            class="col col-4 sm-4"
            data-event="relatedcourses.click"
          />
        </Slider>
      </div>
      <div v-if="resource.popular.length" class="container most-popular tracking-section" data-event="popularresources.scroll">
        <Slider
          v-if="$store.state.layout === 'desktop'"
          :disabled="resource.popular && resource.popular.length <= 3"
          dark
          class-name="resources"
        >
          <h3 slot="heading" class="h3">
            Most Popular {{ resource.categoryName }}
          </h3>
          <p slot="description" class="caption description">
            Check out our most helpful downloadable resources according to 365 Data Science students and our expert team of instructors.
          </p>
          <ResourceCard
            v-for="(resource, idx) in resource.popular"
            :key="idx"
            slot="slide"
            :resource="resource"
            no-pin
            class="col col-3 md-4 sm-6"
            data-event="popularresources.click"
            heading="h4"
          />
        </Slider>
        <div v-else class="resources">
          <h3 class="h3">
            Most Popular {{ resource.categoryName }}
          </h3>
          <p class="caption description">
            Check out our most helpful downloadable resources according to 365 Data Science students and our expert team of instructors.
          </p>
          <div class="row resources-cards">
            <ResourceCard
              v-for="(resource, idx) in resource.popular"
              :key="idx"
              :resource="resource"
              no-pin
              class="col sm-4"
              data-event="popularresources.click"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="download-section container">
      <div class="template row col-8 offset-2 sm-8 sm-offset-2">
        <div class="form col col-6 sm-6">
          <div class="badges">
            <nuxt-link class="cat" :to="`/resources-center/${resource.categorySlug}/`">
              {{ resource.categoryName }}
            </nuxt-link>
            <nuxt-link
              v-if="resource.technology" class="technology" :class="resource.technologySlug"
              :to="`/resources-center/${resource.categorySlug}?technology=${resource.technologySlug}`"
            >
              {{ resource.technology }}
            </nuxt-link>
          </div>
          <p class="h3">
            {{ resource.name }}
          </p>
          <DownloadForm
            class="bottom-form"
            hide-fields
            :exam="resource.categorySlug === 'practice-exams'"
            :exam-id="resource.examId"
            :category="resource.categorySlug"
            data-event="download2.click"
          />
        </div>
        <div class="image col col-6 sm-6">
          <img loading="lazy" :src="`${$config.imagesUrl}/resources/thumb@480_${resource.image}`" :alt="resource.imageAlt">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import share from '@itbpbg/lms-components/src/mixins/share';
import global from '@itbpbg/lms-components/src/mixins/global';
import Breadcrumb from '@itbpbg/lms-components/src/components/Breadcrumb';
import Slider from '@itbpbg/lms-components/src/components/Slider.vue';
import Card from '@itbpbg/lms-components/src/Courses/Card.vue';
import TwitterLogo from '../../../assets/svg/icon-twitter.svg?vue-component';
import FacebookLogo from '../../../assets/svg/icon-facebook.svg?vue-component';
import LinkedinLogo from '../../../assets/svg/icon-linkedin2.svg?vue-component';
import ResourceCard from '../../../components/Resources/ResourceCard';
import DownloadForm from '../../../components/Resources/DownloadForm';

export default {
  components: {
    Breadcrumb,
    Slider,
    Card,
    TwitterLogo,
    FacebookLogo,
    LinkedinLogo,
    ResourceCard,
    DownloadForm,
  },
  mixins: [share, global],
  scrollToTop: true,
  async asyncData({ $axios, route, error }) {
    const resource = await $axios.$get(`/resources/${route.params.slug}`).catch(() => false);
    if (!resource) {
      return error({ statusCode: 404, message: 'Page not found' });
    }
    return { resource };
  },
  data() {
    return {
      src:
        'https://storage.googleapis.com/lms-resources-development/resources/365-Data-Science_Career.Guide.pdf',
      publicPages: 3,
    };
  },
  head() {
    const metaTitle = this.resource.metaTitle || this.resource.name;
    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: { ...this.resource, metaTitle } },
    ]);

    return {
      titleTemplate: `${metaTitle}%s`,
      meta: this.metaProps({
        ...this.resource,
        metaDescription: `${this.resource.metaDescription}`,
      }),
      link: [
        { rel: 'canonical', href: this.canonicalURL() },
      ],
      script: [linkedData],
    };
  },
  computed: {
    url() {
      return `${this.$config.appUrl.replace(/^(https?:\/\/)/, '')}/resources-center/${this.resource.categorySlug}/${this.$route.params.slug}`;
    },
    breadcrumbPcs() {
      return [
        { slug: '/resources-center/', name: 'Resources' },
        { slug: `/resources-center/${this.resource.categorySlug}/`, name: this.resource.categoryName },
        ...(this.resource.technology ? [{ slug: `/resources-center/${this.resource.categorySlug}/?technology=${this.resource.technologySlug}`, name: this.resource.technology }] : []),
        { slug: '', name: this.resource.name },
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.badges {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: var(--spacing-16);

  a,
  span {
    padding: 3px 10px 1px;
    font-weight: 500;
    font-size: var(--spacing-14);
    line-height: 18px;
    text-align: center;
    margin-right: var(--spacing-12);
    border-radius: 100px;
    background: rgba(255, 255, 255, 0.16);
    color: var(--main-light);

    &.technology {
      text-transform: capitalize;
    }
  }
}

::v-deep .start-exam {
  height: 50px;
  margin-top: var(--spacing-24);
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 800px) {
    max-width: 100%;
    width: 100%;
    height: 56px;
  }
}

::v-deep .mobile-warning {
  max-width: 308px;

  @media screen and (max-width: 800px) {
    max-width: 100%;
  }
}

::v-deep .download-resource-form {
  margin: 0;

  .download-resource {
    width: 100%;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 12px;
    font-weight: 600;
    line-height: 120%;
  }
}

::v-deep .bottom-form {
  @include bp($bp-mobile) {
    .input-fields {
      margin-bottom: 0;
    }
  }
}

.resource-specific {
  position: relative;
  background-color: var(--main-dark);
  z-index: 0;

  &:after {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    background: linear-gradient(202.46deg, rgba(0, 164, 178, 0.2) 0%, rgba(0, 164, 178, 0) 59.22%);
    transform: matrix(-1, 0, 0, 1, 0, 0);
    z-index: -1;
  }

  .main-info {
    padding-top: 127px;
    padding-bottom: 180px;

    .resource-info {
      margin-top: 76px;

      .h2 {
        color: var(--main-light);
        max-width: 55%;
      }

      .h4 {
        color: var(--main-light-shade-4);
      }

      .image-wrapper {
        display: flex;
        justify-content: space-between;
        width: 326px;
        height: 430px;
        float: right;
        margin: 0 0 var(--spacing-48) var(--spacing-60);

        .left-group {
          width: 273px;
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          .image {
            display: block;
            border-radius: 11px;
            width: 100%;
          }
        }

        .icons {
          display: flex;
          flex-direction: column;
          row-gap: 24px;
          width: 24px;

          .social:first-of-type {
            path[fill] {
              fill: var(--main-light-shade-4);
            }
          }
        }
      }

      .instructor-card {
        display: flex;
        align-items: center;
        margin-bottom: var(--spacing-36);

        .card-image {
          width: 50px;
          height: 50px;
          border-radius: 100%;
          object-fit: cover;
          object-position: center;
        }

        .card-section {
          padding-left: var(--spacing-12);
          display: flex;
          flex-direction: column;
          justify-content: space-between;

          .h6 {
            color: var(--main-light);
            margin: 0;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: flex-start;

            svg {
              display: block;
              margin-left: var(--spacing-6);
              width: 20px;
              height: 20px;

              path[fill] {
                fill: #2C81E9;
              }
            }
          }

          .founder-info {
            text-transform: capitalize;
            line-height: 100%;
            color: var(--main-light-shade-4);
            margin-top: var(--spacing-8);
          }
        }
      }

      ::v-deep .summary {
        color: var(--main-light-shade-4);

        p {
          &:first-of-type {
            margin-top: 0;
          }

          &:last-of-type {
            margin-bottom: 0;
          }
        }
      }
    }

    .our-mission {
      padding-top: var(--spacing-48);

      .h3 {
        font-size: 24px;
        color: var(--main-light);
        margin-bottom: var(--spacing-24);
      }

      .mission-description {
        color: var(--main-dark-shade-4);

        p:first-of-type {
          margin-top: 0;
        }

        p:last-of-type {
          margin-bottom: 0;
        }
      }
    }

    .benefits {
      padding-top: var(--spacing-48);

      .h3 {
        font-size: 24px;
        color: var(--main-light);
        margin-bottom: var(--spacing-24);
      }

      .body-1 {
        color: var(--main-dark-shade-4);
      }
    }
  }

  .know-stuff {
    background-color: var(--main-dark-shade-1);
    padding-top: 140px;

    .found-courses {
      ::v-deep .slider-container {
        .slider-header {
          margin-bottom: var(--spacing-24);

          .h3 {
            margin-bottom: 0;
            max-width: 370px;
            text-transform: capitalize;
            color: var(--main-light);
          }
        }

        .swiper {
          .course-card {
            margin-bottom: 0;

            .course-card-image {
              height: 220px;
            }

            .course-card-body {
              padding: var(--spacing-16) var(--spacing-24);

              .info {
                order: 1;
                margin-bottom: var(--spacing-8);

                span {
                  font-weight: 400;
                  font-size: 16px;
                  color: var(--main-light);
                }

                .dot {
                  background-color: var(--main-light);
                }
              }

              .course-name {
                order: 2;
                margin-bottom: var(--spacing-16);
                min-height: 40px;
              }

              .instructors {
                order: 3;
                font-weight: 500;
                line-height: 18px;
              }
            }
          }
        }
      }
    }

    .most-popular {
      padding-top: 140px;
      padding-bottom: 147px;

      ::v-deep .slider-container {
        .slider-header {
          .h3 {
            margin-bottom: 0;
            color: var(--main-light);
          }
        }

        .description {
          margin: var(--spacing-16) 0 var(--spacing-36) 0;
          max-width: 670px;
          font-size: 18px;
          line-height: 150%;
          color: var(--main-dark-shade-4);
        }

        .swiper {
          .resource {
            .info {
              padding-bottom: var(--spacing-20);

              .badges {
                padding-right: var(--spacing-16);
              }

              .info-wrapper {
                .category {
                  margin-top: var(--spacing-24);
                  overflow: hidden;
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-line-clamp: 2;
                  line-clamp: 2;
                  -webkit-box-orient: vertical;
                }

                .caption {
                  line-height: 17px;
                }

                .btn-arrow {
                  font-size: 14px;
                }
              }
            }
          }
        }
      }
    }
  }

  .download-section {
    padding: var(--spacing-120) 0;

    .template {
      .form .h3 {
        color: var(--true-white);
        margin-bottom: var(--spacing-36);
      }

      .image {
        img {
          display: block;
          width: 100%;
          border-radius: 8px;
        }
      }
    }
  }
}

@include bp($bp-laptop) {
  .resource-specific .main-info .resource-info .image-wrapper {
    width: 280px;
    height: 369px;

    .left-group {
      width: 225px;
    }
  }
}

@include bp($bp-desktop) {
  .resource-specific .download-section .template .image {
    padding-left: var(--spacing-60);
  }
}

@include bp($bp-mobile) {
  .resource-specific {
    .main-info {
      padding-top: 98px;
      padding-bottom: var(--spacing-80);

      .resource-info {
        margin-top: var(--spacing-36);

        .image-wrapper.extended {
          margin-bottom: var(--spacing-140);
        }

        .h2 {
          font-size: 32px;
          line-height: 35px;
        }

        .instructor-card {
          .card-section {
            .founder-info {
              font-size: 14px;
            }
          }
        }
      }

      .our-mission,
      .benefits {
        .h3 {
          font-size: 21px;
        }
      }
    }

    .know-stuff {
      padding-top: var(--spacing-80);
      padding-bottom: var(--spacing-44);

      .found-courses {
        ::v-deep .slider-container {
          .slider-header {
            .h3 {
              font-size: 28px;
            }
          }
        }
      }

      .most-popular {
        padding-top: var(--spacing-56);
        padding-bottom: var(--spacing-36);

        .h3 {
          margin-bottom: 0;
          font-size: 28px;
          color: var(--main-light);
        }

        .description {
          margin: var(--spacing-16) 0 var(--spacing-36) 0;
          max-width: 670px;
          font-size: 18px;
          line-height: 150%;
          color: var(--main-dark-shade-4);
        }

        .row {
          margin: 0 -12px;

          ::v-deep .col {
            margin-bottom: var(--spacing-30);

            .info-wrapper {
              .category {
                margin-top: var(--spacing-24);
                height: 40px;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                line-clamp: 2;
                -webkit-box-orient: vertical;
              }
            }
          }
        }
      }
    }

    .download-section {
      padding-top: var(--spacing-80);
      padding-bottom: var(--spacing-80);
    }
  }

  @media screen and (max-width: 800px) {
    .resource-specific {
      .main-info {
        .resource-info {
          .image-wrapper {
            float: none;
            margin: 0 0 var(--spacing-60) 0;
            height: auto;
            width: 100%;
            flex-direction: column-reverse;
            flex-wrap: wrap;

            &.extended {
              margin-bottom: var(--spacing-60);
            }

            .icons {
              flex-direction: row;
              align-items: center;
              width: 100%;
              column-gap: 16px;
              margin-bottom: var(--spacing-24);

              .social {
                svg {
                  width: 24px;
                }
              }
            }

            .left-group {
              max-width: 370px;
              width: 100%;

              img {
                margin-bottom: var(--spacing-24);
              }

              ::v-deep .download-resource-form {
                .download-resource {
                  height: 56px;
                }
              }
            }
          }

          .badges {
            margin-bottom: var(--spacing-36);
          }

          .h2 {
            max-width: 100%;
            margin-bottom: var(--spacing-36);
          }
        }
      }

      .know-stuff {
        .found-courses {
          ::v-deep .slider-container {
            .slider-header {
              margin-bottom: var(--spacing-30);
            }

            .swiper {
              .course-card {
                max-width: 50%;
                flex: 0 0 50%;
              }
            }
          }
        }

        .most-popular {
          .resources {
            .caption {
              max-width: 100%;
            }

            .resources-cards {
              .col {
                max-width: 50%;
                flex: 0 0 50%;
              }
            }
          }
        }
      }

      .download-section {
        .template {
          max-width: 100%;
          align-items: flex-start;
          flex-direction: column-reverse;
          row-gap: 48px;
          margin: 0;
          width: 100%;

          .image {
            margin: 0 auto;
          }

          .form {
            margin: 0 auto;
          }
        }
      }
    }
  }
}

@include bp($bp-phone) {
  .resource-specific {
    .main-info {
      .resource-info {
        .image-wrapper {
          .left-group {
            max-width: 100%;
          }
        }
      }
    }

    .know-stuff {
      padding-top: 74px;

      .found-courses {
        ::v-deep .slider-container {
          .swiper {
            .course-card {
              flex: 0 0 100%;
              max-width: 100%;

              .course-card-image {
                height: 187px;
              }

              .course-card-body {
                .course-name {
                  margin-bottom: var(--spacing-8);
                }
              }
            }
          }
        }
      }

      .most-popular {
        .resources {
          .resources-cards {
            ::v-deep .col {
              max-width: 100%;
              flex: 0 0 100%;
              height: fit-content;

              .resource-image {
                height: 240px;
              }

              .info-wrapper {
                .caption {
                  margin-top: var(--spacing-8);
                  height: 50px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-line-clamp: 3;
                  line-clamp: 3;
                  -webkit-box-orient: vertical;
                }
              }
            }
          }
        }
      }
    }

    .download-section {
      .template {
        .form {
          .h3 {
            margin-bottom: var(--spacing-48);
          }

          ::v-deep .bottom-form {
            max-width: 100%;

            .download-resource {
              max-width: 100%;
            }
          }
        }
      }
    }
  }
}
</style>
